// chatStore.ts
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface Message {
  text: string;
  isBot: boolean;
  role: "system" | "user" | "assistant";
  timestamp: Date;
  isError?: boolean;
  data?: any;
}

interface ChatState {
  messages: Message[];
  lessonChats: Record<string, Message[]>;
  exerciseChats: Record<string, Message[]>;
  returningMetadata: any;
  dashboardState: string;
  retroStep: string;
  showGreeting: boolean;
  showRetroText: boolean;
  showQuickReplies: boolean;
  quickReplyOptions: Array<{ label: string; value: string }>;
  setReturningMetadata: (metadata: any) => void;
  addMessage: (message: Message) => void;
  addUserMessage: (text: string) => void;
  addAssistantMessage: (text: string, isError?: boolean, data?: any) => void;
  
  // Custom registers handlers
  addLessonMessage: (lessonId: string, message: Message) => void;
  addExerciseMessage: (exerciseId: string, message: Message) => void;
  clearLessonChat: (lessonId: string) => void;
  
  getFormattedHistory: (limit?: number) => { role: "user" | "assistant" | "system", content: string }[];
  clearMessages: () => void;
  setDashboardState: (dashboardState: string) => void;
  setRetroStep: (retroStep: string) => void;
  setShowGreeting: (showGreeting: boolean) => void;
  setShowRetroText: (showRetroText: boolean) => void;
  setShowQuickReplies: (showQuickReplies: boolean) => void;
  setQuickReplyOptions: (quickReplyOptions: Array<{ label: string; value: string }>) => void;
}

export const useChatStore = create(
  persist<ChatState>(
    (set) => ({
      messages: [],
      lessonChats: {},
      exerciseChats: {},
      returningMetadata: null,
      dashboardState: 'AWAITING_REMEMBER',
      retroStep: 'none',
      showGreeting: false,
      showRetroText: false,
      showQuickReplies: false,
      quickReplyOptions: [],
      setReturningMetadata: (metadata) => set({ returningMetadata: metadata }),
      setDashboardState: (dashboardState) => set({ dashboardState }),
      setRetroStep: (retroStep) => set({ retroStep }),
      setShowGreeting: (showGreeting) => set({ showGreeting }),
      setShowRetroText: (showRetroText) => set({ showRetroText }),
      setShowQuickReplies: (showQuickReplies) => set({ showQuickReplies }),
      setQuickReplyOptions: (quickReplyOptions) => set({ quickReplyOptions }),
      addMessage: (message) =>
        set((state) => ({
          messages: [
            ...state.messages,
            {
              ...message,
              isBot: message.role === "assistant",
              timestamp: message.timestamp || new Date(),
            },
          ],
        })),
      addUserMessage: (text) => {
        const { addMessage } = useChatStore.getState();
        addMessage({
          text,
          isBot: false,
          role: "user",
          timestamp: new Date(),
        });
      },
      addAssistantMessage: (text, isError, data) => {
        const { addMessage } = useChatStore.getState();
        addMessage({
          text,
          isBot: true,
          role: "assistant",
          timestamp: new Date(),
          isError,
          data,
        });
      },
      addLessonMessage: (lessonId, message) =>
        set((state) => {
          const thread = state.lessonChats[lessonId] || [];
          return {
            lessonChats: {
              ...state.lessonChats,
              [lessonId]: [
                ...thread,
                {
                  ...message,
                  isBot: message.role === "assistant",
                  timestamp: message.timestamp || new Date(),
                },
              ],
            },
          };
        }),
      addExerciseMessage: (exerciseId, message) =>
        set((state) => {
          const thread = state.exerciseChats[exerciseId] || [];
          return {
            exerciseChats: {
              ...state.exerciseChats,
              [exerciseId]: [
                ...thread,
                {
                  ...message,
                  isBot: message.role === "assistant",
                  timestamp: message.timestamp || new Date(),
                },
              ],
            },
          };
        }),
      clearLessonChat: (lessonId) =>
        set((state) => {
          const newLessonChats = { ...state.lessonChats };
          delete newLessonChats[lessonId];
          return { lessonChats: newLessonChats };
        }),
      getFormattedHistory: (limit = 10) => {
        const state = useChatStore.getState();
        return state.messages.slice(-limit).map((m) => ({
          role: m.role,
          content: m.text,
        }));
      },
      clearMessages: () => set({ 
        messages: [], 
        lessonChats: {},
        exerciseChats: {},
        returningMetadata: null,
        dashboardState: 'AWAITING_REMEMBER',
        retroStep: 'none',
        showGreeting: false,
        showRetroText: false,
        showQuickReplies: false,
        quickReplyOptions: []
      }),
    }),
    {
      name: 'shoonit-chat',
      storage: createJSONStorage(() => localStorage),
      serialize: (state: any) => {
        const serializeMessage = (msg: any) => ({
          ...msg,
          timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : new Date(msg.timestamp).toISOString(),
        });
        
        const serializedState = {
          ...state,
          state: {
            ...state.state,
            messages: (state.state.messages || []).map(serializeMessage),
            lessonChats: Object.fromEntries(
              Object.entries(state.state.lessonChats || {}).map(([id, thread]: [string, any]) => [
                id,
                thread.map(serializeMessage)
              ])
            ),
            exerciseChats: Object.fromEntries(
              Object.entries(state.state.exerciseChats || {}).map(([id, thread]: [string, any]) => [
                id,
                thread.map(serializeMessage)
              ])
            )
          },
        };
        return JSON.stringify(serializedState);
      },
      deserialize: (str: any) => {
        const parsed = JSON.parse(str);
        const deserializeMessage = (msg: any) => {
          const timestamp = new Date(msg.timestamp);
          return {
            ...msg,
            timestamp: isNaN(timestamp.getTime()) ? new Date() : timestamp,
          };
        };

        return {
          ...parsed,
          state: {
            ...parsed.state,
            messages: (parsed.state.messages || []).map(deserializeMessage),
            lessonChats: Object.fromEntries(
              Object.entries(parsed.state.lessonChats || {}).map(([id, thread]: [string, any]) => [
                id,
                thread.map(deserializeMessage)
              ])
            ),
            exerciseChats: Object.fromEntries(
              Object.entries(parsed.state.exerciseChats || {}).map(([id, thread]: [string, any]) => [
                id,
                thread.map(deserializeMessage)
              ])
            )
          },
        };
      },
    }
  )
);

export default useChatStore;